import { htExcelDateFromIso } from './htDateUtils';
import {
  formatHtQuantityTreated,
  formatHtTreatmentDateDisplay,
  formatHtWeightKg,
} from './htDisplayFormat';
import { applyHtBordersFromTemplate } from './htBorders';
import {
  applyHtLabelValuePairRowHeights,
  applyHtLabelValueRowHeight,
} from './htSheetConsignorHeights';
import { HT_FONT } from './htSheetConstants';
import { writeHtFooterExcel } from './htSheetFooter';
import { HT_PART1_ROW_HEIGHTS, writeHtPart1Excel } from './htSheetPart1';

export { HT_FONT };
export const HT_COL_START = 2;
export const HT_COL_END = 5;
export const HT_ROW_START = 5;
export const HT_ROW_END = 65;

/** Uniones de la hoja HT en Invoice 5019 (área C5:E65). */
export const HT_MERGES = [
  [13, 3, 14, 4],
  [15, 3, 16, 4],
  [13, 5, 16, 5],
  [17, 3, 18, 5],
  [19, 3, 19, 4],
  [20, 3, 26, 4],
  [20, 5, 26, 5],
  [27, 4, 28, 5],
  [29, 4, 30, 5],
  [31, 4, 31, 5],
  [32, 4, 32, 5],
  [33, 3, 33, 5],
  [34, 3, 34, 5],
  [35, 3, 35, 5],
  [42, 3, 42, 5],
  [44, 3, 44, 5],
  [51, 3, 51, 5],
  [53, 3, 53, 5],
];

export const HT_ROW_HEIGHTS = {
  ...HT_PART1_ROW_HEIGHTS,
  19: 14.55,
  20: 13.05,
  21: 13.05,
  22: 13.05,
  23: 13.05,
  24: 13.05,
  25: 13.05,
  26: 13.05,
  27: 15,
  28: 13.8,
  29: 13.8,
  30: 13.8,
  31: 14.4,
  32: 14.4,
  33: 32.55,
  34: 37.8,
  35: 13.8,
  36: 37.8,
  37: 25.2,
  38: 12.45,
  39: 34.05,
  40: 25.2,
  43: 21.45,
  44: 36,
  45: 12.45,
  48: 15.6,
  52: 12.45,
  53: 13.8,
  54: 13.8,
  55: 13.8,
  56: 13.8,
  57: 15,
  58: 15,
  59: 15,
  60: 15,
  61: 15,
  62: 15,
  63: 14.4,
  64: 21,
  65: 13.8,
};

/** Columnas A–E (plantilla Invoice 5019 HT; impresión C5:E65). */
export const HT_COL_WIDTHS = [2.5, 12.22, 41.44, 54.44, 66.78];

function setCell(ws, r, c, value, opts = {}) {
  const cell = ws.getCell(r, c);
  cell.value = value == null || value === '' ? null : value;
  cell.font = {
    ...HT_FONT,
    ...(opts.bold ? { bold: true } : {}),
    ...(opts.italic ? { italic: true } : {}),
  };
  cell.alignment = {
    vertical: opts.align?.vertical ?? 'top',
    horizontal: opts.align?.horizontal ?? 'left',
    wrapText: opts.wrap !== false,
  };
}

export function prepareHtSheetStructure(ws) {
  for (let r = HT_ROW_START; r <= HT_ROW_END; r += 1) {
    const h = HT_ROW_HEIGHTS[r];
    if (h) ws.getRow(r).height = h;
  }
  HT_MERGES.forEach(([r1, c1, r2, c2]) => {
    if (r2 > r1 || c2 > c1) ws.mergeCells(r1, c1, r2, c2);
  });
}

export function applyHtSheetBorders(ws) {
  applyHtBordersFromTemplate(ws);
}

/** Valores en celdas (columnas C=3, D=4, E=5; logo en B). */
export function writeHtSheetValues(ws, params) {
  const S = params.static;

  writeHtPart1Excel(ws, params, HT_FONT);

  const prodHdr = { align: { vertical: 'middle', horizontal: 'center' }, wrap: true, bold: true };
  const prodCenter = { align: { vertical: 'middle', horizontal: 'center' }, bold: true };
  setCell(ws, 19, 3, S.HT_PRODUCTS_HEADER_LINE, prodHdr);
  setCell(ws, 19, 5, S.HT_QTY_HEADER_LINE, prodHdr);
  setCell(ws, 20, 3, S.HT_PRODUCTS_VALUE, prodCenter);
  setCell(ws, 20, 5, params.quantityProducts, prodCenter);

  const consignorAlign = { align: { vertical: 'middle', horizontal: 'left' }, bold: true };
  setCell(ws, 27, 3, S.HT_EXPORTADOR_LABEL, consignorAlign);
  setCell(ws, 27, 4, S.HT_EXPORTADOR_VALUE, consignorAlign);
  setCell(ws, 28, 3, S.HT_EXPORTADOR_LABEL_EN, consignorAlign);
  setCell(ws, 29, 3, S.HT_IMPORTADOR_LABEL, consignorAlign);
  setCell(ws, 29, 4, params.consigneeAddress, consignorAlign);
  setCell(ws, 30, 3, S.HT_IMPORTADOR_LABEL_EN, consignorAlign);
  setCell(ws, 31, 3, S.HT_ORIGIN_LABEL, consignorAlign);
  setCell(ws, 31, 4, params.origin, consignorAlign);
  setCell(ws, 32, 3, S.HT_DEST_LABEL, consignorAlign);
  setCell(ws, 32, 4, params.destination, consignorAlign);

  applyHtLabelValuePairRowHeights(
    ws,
    27,
    28,
    [S.HT_EXPORTADOR_LABEL, S.HT_EXPORTADOR_LABEL_EN],
    S.HT_EXPORTADOR_VALUE
  );
  applyHtLabelValuePairRowHeights(
    ws,
    29,
    30,
    [S.HT_IMPORTADOR_LABEL, S.HT_IMPORTADOR_LABEL_EN],
    params.consigneeAddress
  );
  applyHtLabelValueRowHeight(ws, 31, S.HT_ORIGIN_LABEL, params.origin);
  applyHtLabelValueRowHeight(ws, 32, S.HT_DEST_LABEL, params.destination);

  const modLeft = { align: { vertical: 'middle', horizontal: 'left' }, bold: true };
  const modCenter = { align: { vertical: 'middle', horizontal: 'center' }, bold: true };
  setCell(ws, 33, 3, S.HT_MODALITY_TITLE, modLeft);
  setCell(ws, 34, 3, S.HT_MODALITY_LINE, modCenter);
  setCell(ws, 35, 3, S.HT_MODALITY_SUB, {
    align: { vertical: 'middle', horizontal: 'center' },
    italic: true,
    bold: true,
  });
  const treatLabel = { align: { vertical: 'middle', horizontal: 'left' }, bold: true, wrap: true };
  const treatValue = { align: { vertical: 'middle', horizontal: 'left' }, wrap: true };
  setCell(ws, 36, 3, S.HT_LOT_LINE, treatLabel);
  setCell(ws, 36, 5, S.HT_CYCLE_LINE, treatValue);
  setCell(ws, 37, 3, S.HT_QTY_TREATED_LABEL, treatLabel);
  setCell(ws, 37, 4, formatHtQuantityTreated(params.quantityTreated), treatValue);
  setCell(ws, 37, 5, S.HT_TREATMENT_PARAMS_LABEL, treatLabel);
  setCell(ws, 38, 3, S.HT_SCIENTIFIC_LABEL, treatLabel);
  setCell(ws, 38, 4, S.HT_SCIENTIFIC_VALUE.trim(), treatValue);
  setCell(ws, 38, 5, S.HT_TEMP_ACTUAL, treatLabel);
  setCell(ws, 39, 3, S.HT_DATE_TREAT_LABEL, treatLabel);
  setCell(ws, 39, 4, formatHtTreatmentDateDisplay(params.treatmentDateIso), treatValue);
  setCell(ws, 39, 5, S.HT_TEMP_PARAM, treatLabel);
  setCell(ws, 40, 3, S.HT_TIME_LABEL, treatLabel);
  setCell(ws, 40, 4, S.HT_TIME_VALUE.trim(), treatValue);
  setCell(ws, 40, 5, S.HT_EXPOSURE, treatLabel);
  const placeCell = ws.getCell(42, 3);
  placeCell.value = {
    richText: [
      { font: { ...HT_FONT, bold: true }, text: S.HT_PLACE_TREAT_LABEL },
      { font: { ...HT_FONT }, text: ` ${S.HT_PLACE_TREAT_SUFFIX}` },
    ],
  };
  placeCell.alignment = {
    vertical: 'middle',
    horizontal: 'center',
    wrapText: true,
  };
  setCell(ws, 43, 3, S.HT_REMARK_LABEL, treatLabel);
  setCell(ws, 43, 4, params.remarkBlInvoice, treatValue);
  setCell(ws, 44, 3, S.HT_SHIPPING_DESC, modCenter);
  const shipLabel = { align: { vertical: 'middle', horizontal: 'left' }, bold: true, wrap: false };
  const shipValue = { align: { vertical: 'middle', horizontal: 'left' }, wrap: false };
  setCell(ws, 45, 3, S.HT_GROSS_LABEL, shipLabel);
  setCell(ws, 45, 4, formatHtWeightKg(params.grossWeight), shipValue);
  setCell(ws, 46, 3, S.HT_NET_LABEL, shipLabel);
  setCell(ws, 46, 4, formatHtWeightKg(params.netWeight), shipValue);
  setCell(ws, 47, 3, S.HT_VOLUME_LABEL, shipLabel);
  setCell(ws, 47, 4, formatHtQuantityTreated(params.volumeDescription), shipValue);
  setCell(ws, 48, 4, S.HT_TRANSPORT_HEADER, modCenter);
  const shipCenter = { align: { vertical: 'middle', horizontal: 'center' }, bold: true, wrap: false };
  setCell(ws, 50, 3, S.HT_ROAD, shipCenter);
  setCell(ws, 50, 4, S.HT_AIR, shipCenter);
  setCell(ws, 50, 5, S.HT_SEA, shipCenter);
  setCell(ws, 52, 3, S.HT_MARKS_LABEL, shipLabel);
  setCell(ws, 52, 4, params.distinctiveMark ?? '', shipValue);
  setCell(ws, 53, 3, S.HT_MARKS_SUB, {
    align: { vertical: 'middle', horizontal: 'left' },
    italic: true,
  });
  writeHtFooterExcel(ws, params);
}
